import React, { useEffect, useRef, useState } from "react";

function getSessionIdFromHash() {
  // hash looks like: "#/booking-success?session_id=cs_..."
  const hash = window.location.hash || "";
  const qIndex = hash.indexOf("?");
  if (qIndex === -1) return null;

  const queryString = hash.substring(qIndex + 1);
  const params = new URLSearchParams(queryString);
  return params.get("session_id");
}

export default function BookingSuccess({ onNavigate }) {
  const [state, setState] = useState({ status: "checking", msg: "", amendment: false });
  const verificationStartedRef = useRef(false);

  useEffect(() => {
    if (verificationStartedRef.current) return;
    verificationStartedRef.current = true;

    const sessionId = getSessionIdFromHash();
    if (!sessionId) {
      setState({
        status: "failed",
        msg: "Missing Stripe session id. If you paid, contact us and we’ll sort it.",
      });
      return;
    }

    (async () => {
      try {
        const res = await fetch("/.netlify/functions/verify-checkout-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });

        const json = await res.json();

        if (!res.ok) {
          setState({
            status: "failed",
            msg: json?.error || "Couldn’t verify payment yet.",
          });
          return;
        }

        const amendment = Boolean(json?.amendment);

        setState({
          status: "ok",
          amendment,
          msg: json?.bookingId
            ? amendment
              ? `Additional payment confirmed. Booking updated: ${json.bookingId}`
              : `Payment confirmed. Booking reference: ${json.bookingId}`
            : amendment
              ? "Additional payment confirmed. Booking updated."
              : "Payment confirmed.",
        });
      } catch (e) {
        setState({
          status: "failed",
          msg: e?.message || "Something went wrong verifying payment.",
        });
      }
    })();
  }, []);

  const isOk = state.status === "ok";
  const isChecking = state.status === "checking";
  const isFailed = state.status === "failed";

  return (
    <div style={page}>
      <div style={panel}>
        <div style={topRow}>
          <div style={{ ...iconCircle, ...(isOk ? iconOk : isFailed ? iconBad : iconWait) }}>
            {isOk ? "✓" : isFailed ? "!" : "…"}
          </div>

          <div style={{ display: "grid", gap: 4 }}>
            <div style={kicker}>Jampot Rally 2026</div>
            <h1 style={title}>
              {isOk
                ? state.amendment
                  ? "Booking updated"
                  : "Booking confirmed"
                : isFailed
                  ? "Booking not confirmed"
                  : "Confirming your payment..."}
            </h1>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          {isChecking && (
            <>
              <p style={bodyText}>
                Just confirming your payment with Stripe. This usually takes a second.
              </p>
              <div style={progressWrap}>
                <div style={progressBar} />
              </div>
              <p style={mutedText}>
                Don’t refresh if you can help it — but if you do, it’ll still work.
              </p>
            </>
          )}

          {isOk && (
            <>
              <p style={bodyText}>{state.msg}</p>
              <div style={infoBox}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>What happens next?</div>
                <ul style={list}>
                  <li>
                    {state.amendment
                      ? "We have recorded your additions and payment."
                      : "We have recorded your booking and payment."}
                  </li>
                  <li>You can now close this page safely.</li>
                  <li>You will receive an email confirmation for your records.</li>
                </ul>
              </div>

              <div style={btnRow}>
                <button style={btnPrimary} onClick={() => onNavigate("home")}>
                  Back to site
                </button>
                <button style={btnGhost} onClick={() => onNavigate("jampot")}>
                  Rally info
                </button>
              </div>
            </>
          )}

          {isFailed && (
            <>
              <p style={{ ...bodyText, color: "#b00020" }}>{state.msg}</p>

              <div style={infoBoxWarn}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>If you think you paid…</div>
                <div style={mutedText}>
                  It might be a slow confirmation, or the session id is missing. Contact us and we’ll match it up
                  manually.
                </div>
              </div>

              <div style={btnRow}>
                <button style={btnPrimary} onClick={() => onNavigate("jampot")}>
                  Rally info
                </button>
                <button style={btnGhost} onClick={() => onNavigate("home")}>
                  Back to site
                </button>
              </div>
            </>
          )}
        </div>

        <div style={footer}>
          <span style={footerChip}>Secure payment via Stripe</span>
          <span style={footerChip}>Reference kept in Supabase</span>
        </div>
      </div>
    </div>
  );
}

// ---------- styles ----------
const page = {
  minHeight: "70vh",
  display: "grid",
  placeItems: "center",
  padding: 20,
  background: "linear-gradient(180deg, #f6f3ee 0%, #ffffff 60%)",
};

const panel = {
  width: "min(760px, 100%)",
  borderRadius: 18,
  border: "1px solid rgba(0,0,0,0.10)",
  background: "white",
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  padding: 18,
};

const topRow = {
  display: "flex",
  gap: 14,
  alignItems: "center",
  padding: 14,
  borderRadius: 14,
  background:
    "linear-gradient(90deg, rgba(210,194,166,0.45), rgba(225,214,193,0.35))",
  border: "1px solid rgba(0,0,0,0.06)",
};

const iconCircle = {
  width: 44,
  height: 44,
  borderRadius: 999,
  display: "grid",
  placeItems: "center",
  fontWeight: 900,
  fontSize: 20,
  border: "1px solid rgba(0,0,0,0.12)",
  userSelect: "none",
};

const iconOk = { background: "rgba(30,140,80,0.15)" };
const iconBad = { background: "rgba(200,0,0,0.10)" };
const iconWait = { background: "rgba(0,0,0,0.05)" };

const kicker = { fontSize: 12, letterSpacing: "0.08em", fontWeight: 800, opacity: 0.8 };
const title = { margin: 0, fontSize: 26, lineHeight: 1.1 };
const bodyText = { margin: "10px 0", fontSize: 15, lineHeight: 1.5, opacity: 0.95 };
const mutedText = { margin: "8px 0 0", fontSize: 12.5, opacity: 0.75 };

const progressWrap = {
  marginTop: 12,
  height: 10,
  borderRadius: 999,
  overflow: "hidden",
  background: "rgba(0,0,0,0.06)",
  border: "1px solid rgba(0,0,0,0.08)",
};

const progressBar = {
  width: "55%",
  height: "100%",
  borderRadius: 999,
  background: "linear-gradient(90deg, #6f9f8a, #b8d6c8)",
  animation: "pulse 1.1s ease-in-out infinite alternate",
};

const infoBox = {
  marginTop: 12,
  padding: 14,
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.08)",
  background: "rgba(111,159,138,0.08)",
};

const infoBoxWarn = {
  marginTop: 12,
  padding: 14,
  borderRadius: 14,
  border: "1px solid rgba(176,0,32,0.20)",
  background: "rgba(176,0,32,0.06)",
};

const list = { margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 6 };

const btnRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 14,
};

const btnBase = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.18)",
  cursor: "pointer",
  fontWeight: 800,
};

const btnPrimary = {
  ...btnBase,
  background: "linear-gradient(90deg, #1F3A2E, #2a4b3b)",
  color: "white",
  border: "1px solid rgba(0,0,0,0.25)",
};

const btnGhost = {
  ...btnBase,
  background: "white",
};

const footer = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  marginTop: 16,
  paddingTop: 14,
  borderTop: "1px solid rgba(0,0,0,0.08)",
};

const footerChip = {
  fontSize: 12,
  opacity: 0.8,
  border: "1px solid rgba(0,0,0,0.10)",
  borderRadius: 999,
  padding: "5px 10px",
  background: "rgba(0,0,0,0.03)",
};
